import { supabaseFetch } from "./supabaseClient";

const DEFAULT_TIMEOUT_MS = 4000;

export const RENDER_V1 = Object.freeze({ state: "v1" });

/**
 * Asks V2 whether a public share link's project has moved (get_v1_share_redirect,
 * three states):
 *   { state: "v1" }                    render the V1 viewer as today
 *   { state: "redirect", shareLinkId } send the customer to the V2 share page
 *   { state: "gone" }                  terminal "no longer available" page —
 *                                      never the stale frozen V1 copy
 *
 * Fail-open by design, mirroring projectAuthority.js: any error, an unexpected
 * response shape, a redirect with no usable link, or a slow/hung request
 * (bounded by timeoutMs) all report RENDER_V1. A Supabase problem must never
 * take down working V1 share links; the cost of failing open is only that a
 * moved link shows the frozen V1 copy until the next load.
 */
export async function getShareRedirect(
  firebaseProjectId,
  { timeoutMs = DEFAULT_TIMEOUT_MS } = {},
) {
  if (!firebaseProjectId) return RENDER_V1;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const data = await supabaseFetch("/rpc/get_v1_share_redirect", {
      method: "POST",
      body: JSON.stringify({ p_firebase_project_id: firebaseProjectId }),
      signal: controller.signal,
    });
    if (data?.state === "redirect") {
      if (typeof data.share_link_id === "string" && data.share_link_id) {
        return { state: "redirect", shareLinkId: data.share_link_id };
      }
      return RENDER_V1;
    }
    if (data?.state === "gone") return { state: "gone" };
    return RENDER_V1;
  } catch (err) {
    console.warn("Share redirect check failed open:", err?.message ?? err);
    return RENDER_V1;
  } finally {
    clearTimeout(timer);
  }
}
